import React, { useState, useEffect } from "react";
import { RouteComponentProps, Prompt } from "react-router";
import {
  Paper,
  Grid,
  makeStyles,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Box,
  Button,
} from "@material-ui/core";
import * as Yup from "yup";
import useInput from "../../../hooks/input/useInput";
import useSnackbar from "../../../hooks/snackbar/useSnackbar";
import useChangeTracker from "../../../hooks/change-tracker/useChangeTracker";
import ValidationConfiguration from "../../../hooks/change-tracker/ValidationConfiguration";
import TrTable from "../../../components/shared/controls/TrTable";
import TrTableHeadMetadata from "../../../models/shared/TrTableHeadMetadata";
import TrTableBodyData from "../../../models/shared/TrTableBodyData";
import TrSelect from "../../shared/controls/TrSelect";
import TrTextField from "../../shared/controls/TrTextField";
import TrSnackbar from "../../shared/controls/TrSnackbar";
import TrSelectData from "../../../models/shared/TrSelectData";
import ButtonConfirmDialog from "../../shared/common/ButtonConfirmDialog";
import FinancingRequestDetails from "../../../models/admin/financing-request/FinancingRequestDetails";
import FinancingRequestStudentDetails from "../../../models/admin/financing-request/FinancingRequestStudentDetails";
import FinancingRequestStatus from "../../../models/financing-request-status/FinancingRequestStatus";
import FinancingRequestStatusEnum from "../../../models/financing-request-status/FinancingRequestStatusEnum";
import TrackerName from "../../../models/admin/financing-request/FinancingRequestTrackerName";
import FinancingRequestStatusApiClient from "../../../api/FinancingRequestStatusApiClient";
import FinancingRequestApiClient from "../../../api/admin/FinancingRequestApiClient";
import {
  nameof,
  numberFormat,
  currencyFormat,
} from "../../../utilities/utilities";
import useStudentDataInfo from "../../../hooks/student-data-info/useStudentDataInfo";
import { SchoolCarrierDetails } from "../../../models/financing-request/SchoolCarrierDetails";
import { VehicleType } from "../../../models/vehicle-type/VehicleType";
import useCodeBook from "../../../hooks/codebook/useCodeBook";
import { AuthorizedCarrierDetails } from "../../../models/financing-request/AuthorizedCarrierDetails";
import FinancingRequestStatusElement from "../../shared/controls/FinancingRequestStatusElement";
import LockOpenIcon from '@material-ui/icons/LockOpen';
import AnnualAdministration from "../../../models/admin/annual-administration/AnnualAdministration";
import PaymentRequestApiClient from "../../../api/PaymentRequestApiClient";
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import NoteModal from "../../../components/shared/common/NoteModal";
import DecisionDocument from "../../../models/financing-request/DecisionDocument";
import { Link } from "react-router-dom";
import DocumentApiClient from "../../../api/DocumentApiClient";
import ArrowBackIcon from '@material-ui/icons/ArrowBack';


interface MatchParams {
  id: string;
}

interface Props extends RouteComponentProps<MatchParams> { }

export default function FinancingRequestDetailsPage({ match }: Props) {
  const classes = useStyles({});
  const { id } = match.params;

  const [annualAdministration, setAnualAdministration] = useState<
    AnnualAdministration
  >({} as AnnualAdministration);

  const [financingRequestStatuses, setFinancingRequestStatuses] = useState<
    FinancingRequestStatus[]
  >([]);

  const [financingRequest, setfinancingRequest] = useState<
    FinancingRequestDetails
  >({} as FinancingRequestDetails);

  const [showSave, setShowSave] = useState<boolean>(false);

  const [canStatusReturn, setCanStatusReturn] = useState<boolean>(false);

  const { handleTextChange, handleSelectChange } = useInput();
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<string>("");
  const [selectedEntityId, setSelectedEntityId] = useState<number | undefined>();
  const [entityType, setEntityType] = useState<EntityType | undefined>();
  const documentApiClient = new DocumentApiClient();
  const financingRequestApiClient = new FinancingRequestApiClient();

  const {
    isOpened: isSnackbarOpened,
    open: openSnackbar,
    close: closeSnackbar,
    message: snackbarMessage,
    variant: snackbarVariant,
  } = useSnackbar();

  const validationConfig = {
    trackerName: TrackerName.Details,
    validationScheme: Yup.object<FinancingRequestDetails>().shape({
      statusId: Yup.number(),
      changeReason: Yup.string().nullable().when("statusId", {
        is: FinancingRequestStatusEnum.Rejected,
        then: Yup.string().required(),
      }),
    }),
  } as ValidationConfiguration;

  const {
    validationResult,
    updateTrackerDebounceEffect,
    showErrors,
    resetTracker,
  } = useChangeTracker([validationConfig], false);

  const {
    getTransportOptionInfoByStudentId,
    getFoodOptionInfoByStudentId,
    getTeachingAidOptionInfoByStudentId,
  } = useStudentDataInfo({ financingRequestId: financingRequest.id });

  useEffect(() => {
    return updateTrackerDebounceEffect([
      [TrackerName.Details, financingRequest],
    ]);
  }, [financingRequest]);

  useEffect(() => {
    if (financingRequest && financingRequest.id) {
      resetTracker([[TrackerName.Details, financingRequest]]);
    }
  }, [financingRequest]);
  

  useEffect(() => {
    getFinancingRequestStatuses();
    getFinancingRequestDetails();
  }, []);

  useEffect(() => {
    canReturnRequest();
  }, []);

  const { vehicleTypeSelectItems } = useCodeBook({
    getVehicleType: true,
  });

  async function canReturnRequest() {
    const apiClient = new FinancingRequestApiClient();
    const canReturn = await apiClient.getCanStatusReturnRequest(+id)
    setCanStatusReturn(canReturn);
  }


  const handleSave = async () => {
    if (!validationResult.isValid(false)) {
      showErrors();
      openSnackbar(
        "Ne možete spremiti podatke dok ne ispunite sva obavezna polja. Molimo provjerite vrijednosti u crveno označenim poljima.",
        "error"
      );

      return;
    }

    const apiClient = new FinancingRequestApiClient();

    await apiClient
      .updateStatus(
        (id as unknown) as number,
        financingRequest.statusId,
        financingRequest.changeReason
      )
      .then(function (response) {
        openSnackbar("Uspješno ste izmijenili status zahtjeva.", "success");
      });

    setShowSave(false);
    getFinancingRequestDetails();
  };


  const handleReturnStatusSave = async () => {
    const apiClient = new FinancingRequestApiClient();

    await apiClient
      .updateStatus(
        (id as unknown) as number,
        FinancingRequestStatusEnum.Returned,
        ""
      )
      .then(function (response) {
        openSnackbar("Uspješno ste izmijenili status zahtjeva.", "success");
      });

    setShowSave(false);
    getFinancingRequestDetails();
  }

  async function getFinancingRequestStatuses() {
    const apiClient = new FinancingRequestStatusApiClient();
    const statuses = await apiClient.getFinancingRequestStatuses();

    setFinancingRequestStatuses(statuses);
  }

  async function getFinancingRequestDetails() {
    const apiClient = new FinancingRequestApiClient();
    const apiPaymentClient = new PaymentRequestApiClient();
    const requestDetails = await apiClient.getFinancingRequestDetails(id);

    const pricingRequest = await apiPaymentClient.getAnualPricing();
    setAnualAdministration(pricingRequest.filter((price) => {
      return price.schoolYearName === requestDetails.schoolYear;
    })[0]);
    setfinancingRequest(requestDetails);
  }

  type EntityType = "student" | "authorizedCarrier" | "schoolCarrier" | "decision";

  const isNoteEditable = (statusId: number | undefined): boolean => {
    return (
      statusId !== FinancingRequestStatusEnum.Rejected
    );
  };

  const openNoteModal = (note: string, id: number | undefined, entityType: EntityType) => {
    console.log('Opening modal'); // Debug here
    setSelectedNote(note);
    console.log(selectedNote);
    setSelectedEntityId(id);
    setEntityType(entityType);
    setIsNoteModalOpen(true); // Ensure this changes to true
  };


  const closeNoteModal = () => {
    setIsNoteModalOpen(false);
    setSelectedEntityId(undefined);
    setEntityType(undefined);
  };

  const handleSaveNote = (newNote: string) => {
    if (selectedEntityId && entityType) {
      handleNoteChange(selectedEntityId, newNote, entityType);
    }
    closeNoteModal();
  };

  const handleNoteGetInitialValue = () => {
    return selectedNote;
  }


  const handleNoteChange = async (id: number | undefined, newNote: string, entityType: EntityType) => {
    if (id === undefined) return;

    setSelectedNote(newNote);

    setfinancingRequest((prevState) => {
      switch (entityType) {
        case "student":
          return {
            ...prevState,
            studentDetails: prevState.studentDetails.map((student) =>
              student.financingRequestStudentDataId === id ? { ...student, note: newNote } : student
            ),
          };
        case "authorizedCarrier":
          return {
            ...prevState,
            authorizedCarriers: prevState.authorizedCarriers.map((carrier) =>
              carrier.id === id ? { ...carrier, note: newNote } : carrier
            ),
          };
        case "schoolCarrier":
          return {
            ...prevState,
            schoolCarriers: prevState.schoolCarriers.map((carrier) =>
              carrier.id === id ? { ...carrier, note: newNote } : carrier
            ),
          };
        case "decision":
          return {
            ...prevState,
            decisionDocument: { ...prevState.decisionDocument, note: newNote },
          };
        default:
          return prevState;
      }
    });

    switch (entityType) {
      case "student":
        await financingRequestApiClient.updateStudentNote(id, newNote);
        return;
      case "authorizedCarrier":
        await financingRequestApiClient.updateAuthorizedCarrierNote(id, newNote);
        return;
      case "schoolCarrier":
        await financingRequestApiClient.updateSchoolCarrierNote(id, newNote);
        return;
      case "decision":
        await financingRequestApiClient.updateDecisionNote(id, newNote);
        return;
      default:
        return;
    }
  };

  const handleDownloadDocumentClick = async (id: number, type: 'decision' | 'carrier' | 'student' | 'authorized', fileName: string) => {
    if (type === 'decision') {
      let response = await documentApiClient.downloadDecisionPdf(id, fileName);
    } else if (type === 'carrier') {
      let response = await documentApiClient.downloadSchoolCarrierPdf(id, fileName);
    } else if (type === 'student') {
      let response = await documentApiClient.downloadStudentPdf(id, fileName);
    } else if (type === 'authorized') {
      let response = await documentApiClient.downloadAuthorizedCarrierPdf(id, fileName);
    }
  };

  const decisionTableHeadMetadata: TrTableHeadMetadata<DecisionDocument>[] = [
    {
      id: "fileName",
      label: "Naziv odluke",
      dataFunc: (d: DecisionDocument) => (
        <Typography variant="body2">Odluka o potrebi osiguravanja prilagođenog prijevoza</Typography>
      )
    },
    {
      id: "documents",
      label: "Dokumentacija",
      dataFunc: (d: DecisionDocument) => (
        <div>
          {d ? (
            <Link to={{}} onClick={() => handleDownloadDocumentClick(d.id || 0, "decision", d.fileName || "document.pdf")}>
              {d.fileName}
            </Link>
          )
            : (
              <></>
            )}
        </div>
      )
    },
    {
      id: "note",
      label: "Napomena",
      dataFunc: (d: DecisionDocument) => (
        <MailOutlineIcon
          style={{ cursor: 'pointer' }}
          color={d.note ? "error" : "action"}
          onClick={() => openNoteModal(d.note || "", d.id, "decision")}
        />
      )
    }
  ];

  const decisionTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: [financingRequest.decisionDocument]
  };


  const schoolCarrierTableHeadMetadata: TrTableHeadMetadata<
    SchoolCarrierDetails
  >[] = [
      {
        id: "vehicleType",
        label: "Vrsta vozila",
        dataFunc: (sc: SchoolCarrierDetails) => {
          if (sc.vehicleType && vehicleTypeSelectItems) {
            const vehicleType = vehicleTypeSelectItems.find(
              (vt) => vt.value == sc.vehicleType
            );
            if (vehicleType) return vehicleType.text;
          }
          return "";
        },
      },
      {
        id: "registrationPlate",
        label: "Registracijske oznake",
        dataName: "registrationPlate",
      },
      {
        id: "dailyMileageTotal",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileageTotal",
        dataFunc: (sc: SchoolCarrierDetails) => {
          return numberFormat(sc.dailyMileageTotal || 0, "km", 4);
        },
      },
      {
        id: "numberOfStudents",
        label: "Broj učenika na zahtjevu",
        dataName: "numberOfStudents",
      },
      {
        id: "numberOfStudentsTotal",
        label: "Broj učenika ukupno",
        dataName: "numberOfStudentsTotal",
      },
      {
        id: "documents",
        label: "Dokumentacija",
        dataFunc: (sc: SchoolCarrierDetails) => (
          <div>
            {sc.uploadedDocuments && sc.uploadedDocuments.map((file, index) => (
              <div key={index} style={{ justifyContent: 'space-between', alignItems: 'center', display: 'grid' }}>
                <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "carrier", file.fileName || "document.pdf")}>
                  {file.fileName}
                </Link>
              </div>
            ))}
          </div>
        )
      },
      {
        id: "note",
        label: "Napomena",
        dataFunc: (d: SchoolCarrierDetails) => (
          d.uploadedDocuments && d.uploadedDocuments.length > 0 &&
          <MailOutlineIcon
            style={{ cursor: 'pointer' }}
            color={d.note ? "error" : "action"}
            onClick={() => {
              openNoteModal(d.note || "", d.id, "schoolCarrier");
            }}
          />
        )
      }


    ];

  const schoolCarrierTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: (financingRequest && financingRequest.schoolCarriers) || [],
  };

  const authorizedCarrierTableHeadMetadata: TrTableHeadMetadata<
    AuthorizedCarrierDetails
  >[] = [
      {
        id: "name",
        label: "Naziv prijevoznika",
        dataName: "name",
      },
      {
        id: "dailyMileage",
        label: "Ukupna dnevna kilometraža",
        dataName: "dailyMileage",
        dataFunc: (carrier: AuthorizedCarrierDetails) => {
          return numberFormat(carrier.dailyMileage || 0, "km", 4);
        },
      },
      {
        id: "dailyPrice",
        label: "Dnevna cijena",
        dataName: "dailyPrice",
        dataFunc: (carrier: AuthorizedCarrierDetails) => {
          return currencyFormat(carrier.dailyPrice || 0, financingRequest.currency);
        },
      },
      {
        id: "numberOfStudents",
        label: "Broj učenika na zahtjevu",
        dataName: "numberOfStudents",
      },
      {
        id: "numberOfStudentsTotal",
        label: "Broj učenika ukupno",
        dataName: "numberOfStudentsTotal",
      },
      {
        id: "documents",
        label: "Dokumentacija",
        dataFunc: (ac: AuthorizedCarrierDetails) => (
          <div>
            {ac.uploadedDocuments && ac.uploadedDocuments.map((file, index) => (
              <div key={index} style={{ justifyContent: 'space-between', alignItems: 'center', display: 'grid' }}>
                <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "authorized", file.fileName || "document.pdf")}>
                  {file.fileName}
                </Link>
              </div>
            ))}
          </div>
        )
      },
      {
        id: "note",
        label: "Napomena",
        dataFunc: (d: AuthorizedCarrierDetails) => (
          d.uploadedDocuments && d.uploadedDocuments.length > 0 &&
          <MailOutlineIcon
            color={d.note ? "error" : "action"}
            style={{ cursor: 'pointer' }}
            onClick={() => openNoteModal(d.note || "", d.id, "authorizedCarrier")}
          />
        )
      }
    ];

  const authorizedCarrierTableBodyData: TrTableBodyData = {
    idDataName: "id",
    data: (financingRequest && financingRequest.authorizedCarriers) || [],
  };

  const tableHeadMetadata: TrTableHeadMetadata<
    FinancingRequestStudentDetails
  >[] = [
      {
        id: "lastName",
        label: "Prezime",
        dataName: "lastName",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <>
            <Typography variant="body2">
              {d.lastName + " " + d.firstName}
            </Typography>
            <Typography variant="body2">{d.birthDate}</Typography>
          </>
        ),
      },
      { id: "oib", label: "OIB", dataName: "oib" },
      {
        id: "schoolClass",
        label: "Razred",
        dataName: "schoolClass",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <>
            <Typography variant="body2">{d.programName}</Typography>
            <Typography variant="body2">
              {d.schoolClass + " - " + d.sectionMark}
            </Typography>
          </>
        ),
      },
      {
        id: "vrstaOdjeljenja",
        label: "Vrsta odjeljenja",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <Typography variant="body2">{d.sectionTypeName}</Typography>
        ),
      },
      {
        id: "transportOption",
        label: "Prijevoz",
        dataName: "transportOption",
        dataFunc: (d: FinancingRequestStudentDetails) =>
          getTransportOptionInfoByStudentId(d.studentId, financingRequest.currency),
      },

      {
        id: "foodOption",
        label: "Prehrana",
        dataName: "foodOption",
        dataFunc: (d: FinancingRequestStudentDetails) =>
          getFoodOptionInfoByStudentId(d.studentId),
      },
      {
        id: "teachingAidOption",
        label: "Posebna nastavna sredstva i pomagala",
        dataName: "teachingAidOption",
        dataFunc: (d: FinancingRequestStudentDetails) =>
          getTeachingAidOptionInfoByStudentId(
            d.studentId,
            d.programType,
            d.specialNeedType,
            financingRequest.currency,
            annualAdministration
          ),
      },
      {
        id: "validityRangeColumn",
        label: "Završni mjesec važenja troškova",
        dataName: "validTo",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <>
            <Typography variant="body2">
              {d.validTo.charAt(0).toUpperCase() + d.validTo.slice(1)}
            </Typography>
            <Typography variant="body2">{d.validToReason}</Typography>
          </>
        ),
      },
      {
        id: "documents",
        label: "Dokumentacija",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          <div>
            {d.uploadedDocuments && d.uploadedDocuments.map((file, index) => (
              <div key={index} style={{ justifyContent: 'space-between', alignItems: 'center', display: 'grid' }}>
                <Link to={{}} onClick={() => handleDownloadDocumentClick(file.id || 0, "student", file.fileName || "document.pdf")}>
                  {file.fileName}
                </Link>
              </div>
            ))}
          </div>
        )
      },
      {
        id: "note",
        label: "Napomena",
        dataFunc: (d: FinancingRequestStudentDetails) => (
          d.uploadedDocuments && d.uploadedDocuments.length > 0 &&
          <MailOutlineIcon
            color={d.note ? "error" : "action"}
            style={{ cursor: 'pointer' }}
            onClick={() => openNoteModal(d.note || "", d.financingRequestStudentDataId, "student")}
          />
        )
      }

    ];

  const tableBodyData: TrTableBodyData = {
    idDataName: "studentId",
    data: (financingRequest && financingRequest.studentDetails) || [],
  };

  const handleStatusChange = (e: any) => {
    handleSelectChange(e, setfinancingRequest);
    setShowSave(true);
  };

  return financingRequest.id ? (
    <div className={classes.root}>
      <Grid item xs={12}>
        <Button
          variant="outlined"
          color="primary"
          component={Link}
          to="/admin/financing-requests"
          style={{ marginBottom: "16px" }}
        >
        {<ArrowBackIcon />}
        </Button>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">
            Zahtjev za sufinanciranjem - {financingRequest.schoolName} (
            {financingRequest.schoolPlace})
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardHeader
              title="Detalji zahtjeva"
              titleTypographyProps={{ variant: "h6" }}
              classes={{ title: classes.title }}
              action={canStatusReturn ?
                <ButtonConfirmDialog
                  variant="outlined"
                  btnText={"OTKLJUČAJ"}
                  title={"Izmjena statusa"}
                  message={"Odabirom ove promjene otključat ćete zahtjev. Jeste li sigurni?"}
                  cancelBtnText={"Odustani"}
                  okBtnText={"Otključaj"}
                  onOkClick={() => handleReturnStatusSave()}
                  btnIcon={<LockOpenIcon className={classes.unlockIcon} />}
                /> : <></>
              }
            />
            <Divider />
            <CardContent>
              <Grid container item xs={12} spacing={2}>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Školska godina
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.schoolYear}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Klasifikacijska oznaka
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.classificationMark}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Datum od kada vrijedi zahtjev
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.validFrom}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Broj učenika
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.studentsNumber}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Urudžbeni broj
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.registerNumber}
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Datum do kada vrijedi zahtjev
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {financingRequest.validTo}
                  </Box>
                </Grid>
              </Grid>
              <Grid
                container
                item
                xs={12}
                spacing={2}
                alignItems="center"
                justify="center"
              >
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize" fontWeight="bold">
                    Status zahtjeva
                  </Box>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Box fontSize="body1.fontSize">
                    {!financingRequest.isReviewed ? (
                      <TrSelect
                        label={""}
                        value={financingRequest.statusId}
                        name={nameof<typeof financingRequest>("statusId")}
                        fullWidth={true}
                        selectData={
                          {
                            valueProp: "id",
                            textProp: "name",
                            data: financingRequestStatuses,
                          } as TrSelectData<FinancingRequestStatus>
                        }
                        onChange={handleStatusChange}
                      />
                    ) : (
                      <FinancingRequestStatusElement
                        statusId={financingRequest.statusId}
                        statusName={financingRequest.statusName}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box fontSize="body1.fontSize">
                    {!financingRequest.isReviewed &&
                      financingRequest.statusId ===
                      FinancingRequestStatusEnum.Rejected ? (
                      <TrTextField
                        label="Razlog"
                        name={nameof<typeof financingRequest>("changeReason")}
                        value={financingRequest.changeReason || ""}
                        fullWidth
                        onChange={(e) =>
                          handleTextChange(e, setfinancingRequest)
                        }
                        error={
                          !validationResult.isPropertyValid(
                            TrackerName.Details,
                            nameof<typeof financingRequest>("changeReason")
                          )
                        }
                      />
                    ) : (
                      financingRequest.changeReason
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Box display="inline">
                    {showSave &&
                      financingRequest.statusId !==
                      FinancingRequestStatusEnum.Submitted ? (
                      <ButtonConfirmDialog
                        btnText={"Spremi"}
                        title={"Spremanje izmjena"}
                        message={"Želite li spremiti unesene izmjene?"}
                        cancelBtnText={"Odustani"}
                        okBtnText={"Spremi"}
                        onOkClick={() => handleSave()}
                      />
                    ) : null}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        {financingRequest.decisionDocument ? (
          <Grid item lg={12}>
            <Paper>
              <TrTable
                headMetadata={decisionTableHeadMetadata}
                bodyData={decisionTableBodyData}
              />
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
        {financingRequest.schoolCarriers &&
          financingRequest.schoolCarriers.length > 0 ? (
          <Grid item lg={12}>
            <Paper>
              <TrTable
                headMetadata={schoolCarrierTableHeadMetadata}
                bodyData={schoolCarrierTableBodyData}
              />
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
        {financingRequest.authorizedCarriers &&
          financingRequest.authorizedCarriers.length > 0 ? (
          <Grid item lg={12}>
            <Paper>
              <TrTable
                headMetadata={authorizedCarrierTableHeadMetadata}
                bodyData={authorizedCarrierTableBodyData}
              />
            </Paper>
          </Grid>
        ) : (
          <></>
        )}
        <Grid item lg={12} style={{ display: 'flex' }}>
          <Paper>
            <TrTable
              headMetadata={tableHeadMetadata}
              bodyData={tableBodyData}
            />
          </Paper>
        </Grid>
      </Grid>
      <TrSnackbar
        variant={snackbarVariant}
        isOpened={isSnackbarOpened}
        message={snackbarMessage}
        onClose={closeSnackbar}
      />

      <Prompt
        when={showSave}
        message="Potvrdom ove akcije izgubit ćete podatke koje niste pohranili na trenutnom zahtjevu. Jeste li sigurni da želite odustati od ispunjavanja zahtjeva?"
      />
      <NoteModal
        open={isNoteModalOpen}
        handleClose={closeNoteModal}
        handleSave={handleSaveNote}
        handleNoteGetInitialValue={handleNoteGetInitialValue}
        isEditable={isNoteEditable(financingRequest.statusId)}
      />

    </div>

  ) : null;
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  title: {
    fontWeight: 400,
  },
  unlockIcon: {
    marginRight: theme.spacing(1),
  },
}));
